import { Controller } from "@hotwired/stimulus"

// Keeps the docs left-nav "active" item in sync with the section the reader
// has scrolled to. The nav mixes whole-page links with same-page anchor links
// (e.g. Getting Started and MCP Connect scroll to anchors on one page, while
// Terminology and Fine-Tuning are pages of their own). Only anchor links whose
// path matches the current page take part in scroll tracking; everything else
// is left alone so the server-rendered active state still works.
//
// connect() runs on initial load and on every Turbo navigation.
export default class extends Controller {
  connect() {
    const path = window.location.pathname

    this.sections = [] // { link, target } in document order
    this.topLink = null // same-page link with no anchor (e.g. Overview)

    this.element.querySelectorAll("a.nav-link").forEach((link) => {
      const url = new URL(link.href, window.location.origin)
      if (url.pathname !== path) return

      if (url.hash) {
        const target = document.getElementById(url.hash.slice(1))
        if (target) this.sections.push({ link, target })
      } else {
        this.topLink = link
      }
    })

    if (this.sections.length === 0) return

    this.requestUpdate = this.requestUpdate.bind(this)
    window.addEventListener("scroll", this.requestUpdate, { passive: true })
    window.addEventListener("resize", this.requestUpdate, { passive: true })
    this.update()
  }

  disconnect() {
    if (!this.requestUpdate) return
    window.removeEventListener("scroll", this.requestUpdate)
    window.removeEventListener("resize", this.requestUpdate)
  }

  requestUpdate() {
    if (this.ticking) return
    this.ticking = true
    requestAnimationFrame(() => {
      this.ticking = false
      this.update()
    })
  }

  update() {
    // A section becomes active once its top crosses just below the sticky
    // navbar. The last section above that line wins, so the highlight tracks
    // the section the reader is currently looking at.
    const threshold = 120
    let active = null

    for (const section of this.sections) {
      if (section.target.getBoundingClientRect().top <= threshold) {
        active = section
      } else {
        break
      }
    }

    // At the very bottom of the page the last section may never reach the
    // line, so pin the highlight to it.
    const atBottom =
      window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
    if (atBottom) active = this.sections[this.sections.length - 1]

    this.sections.forEach((section) => {
      section.link.classList.toggle("active", section === active)
    })
    if (this.topLink) this.topLink.classList.toggle("active", active === null)
  }
}
